import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { COMPLIANCE_LABELS, INDUSTRIES, getAddon, getIndustry, getLocale, getPlan } from '@shared/catalog'
import { presetInputFor } from '@shared/pricing'
import { DISCLOSURE_SUMMARY, DISCLOSURE_TITLE } from '@shared/disclosure'
import type { IndustryId, LocaleId, QuoteInput } from '@shared/types'
import { api } from '../lib/api'
import { Banner, Button, Card, Chip, Stepper } from '../ui'
import { PricingConfigurator, QuoteSummary } from '../components/PricingConfigurator'
import { Brand } from '../components/Layout'
import { BlueprintPreview } from '../components/BlueprintPreview'
import { TenantSetup } from '../components/TenantSetup'

const STEPS = ['Industry', 'Configure', 'Database', 'Review'] as const

export function Onboarding() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = useSearch({ strict: false }) as { industry?: string }

  const session = useQuery({ queryKey: ['session'], queryFn: api.session })

  const [step, setStep] = useState(0)
  const [industryId, setIndustryId] = useState<IndustryId>(
    (getIndustry(search.industry ?? '')?.id ?? 'general') as IndustryId,
  )
  const [config, setConfig] = useState<QuoteInput>(() =>
    presetInputFor((getIndustry(search.industry ?? '')?.id ?? 'general') as IndustryId, 'us'),
  )
  // Billing region moved into the Configure step's own PricingConfigurator (showLocale) rather
  // than staying a full-screen step of its own — one fewer screen between signup and a real
  // WhatsApp number, and the region field already lived in that component either way.
  const localeId = config.localeId

  useEffect(() => {
    // A failed session lookup is as unauthenticated as an empty one; without this the
    // page sits on a skeleton forever when the API is unreachable.
    if (session.isError || (session.data && !session.data.user)) {
      navigate({ to: '/login' })
      return
    }
    // Someone who has already finished — via the back button, a bookmark, or a stale tab — used
    // to be walked through all four steps again only to be refused at the end with "Onboarding
    // is already complete", rendered as a bare error with nowhere to go. Send them to the app
    // before they spend the effort.
    if (session.data?.workspace?.onboardingCompletedAt) navigate({ to: '/app' })
  }, [session.data, session.isError, navigate])

  const complete = useMutation({
    mutationFn: () => api.completeOnboarding({ industryId, localeId, config }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['session'] })
      await queryClient.invalidateQueries({ queryKey: ['workspace'] })
      navigate({ to: '/app' })
    },
  })

  // Best-effort, not the only guarantee: `industryId` otherwise lives in this component's
  // state alone until the final "Start trial" call two steps later — but the Database step in
  // between can provision a managed database right now, and seeding reads the industry off the
  // workspace row in the database, not off this state. Saved the moment it's chosen so that
  // read is never still looking at the 'general' default every workspace is created with.
  const saveIndustryChoice = useMutation({
    mutationFn: (id: IndustryId) => api.setStep('configure', id),
  })

  const goTo = (next: number) => {
    setStep(next)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  /**
   * Selecting no longer jumps straight to the next step. The industry decides the labels,
   * fields and reply targets of the whole workspace, so the customer needs a moment to see
   * what each one builds and compare before committing.
   */
  const chooseIndustry = (id: IndustryId) => {
    setIndustryId(id)
    setConfig(presetInputFor(id, localeId, config.billingCycle))
  }

  if (session.isPending) {
    return (
      <div className="container section stack gap-4">
        <div className="skeleton" style={{ height: '32px', width: '240px' }} />
        <div className="skeleton" style={{ height: '200px' }} />
      </div>
    )
  }

  return (
    // Onboarding sits between the two worlds; it takes the app's type voice so the
    // configuration it shows matches how the same numbers read once inside.
    <div className="editorial app-ui stack" style={{ minHeight: '100dvh' }}>
      <header className="app-bar">
        <div className="container app-bar-inner">
          <Brand />
          <span className="body-medium text-muted">
            {session.data?.workspace?.name ?? 'New workspace'}
          </span>
        </div>
      </header>

      <div className="container" style={{ paddingBlock: '32px 16px' }}>
        <Stepper steps={[...STEPS]} current={step} />
      </div>

      <main className="container grow" style={{ paddingBottom: '80px' }}>
        {step === 0 && (
          <IndustryStep
            selected={industryId}
            onSelect={chooseIndustry}
            onContinue={() => {
              saveIndustryChoice.mutate(industryId)
              goTo(1)
            }}
          />
        )}
        {step === 1 && (
          <ConfigureStep
            config={config}
            onChange={setConfig}
            onBack={() => goTo(0)}
            onNext={() => goTo(2)}
          />
        )}
        {step === 2 && <DatabaseStep onBack={() => goTo(1)} onNext={() => goTo(3)} />}
        {step === 3 && (
          <ReviewStep
            config={config}
            industryId={industryId}
            localeId={localeId}
            onBack={() => goTo(2)}
            onConfirm={() => complete.mutate()}
            pending={complete.isPending}
            error={complete.error instanceof Error ? complete.error.message : undefined}
          />
        )}
      </main>
    </div>
  )
}

function StepHeader({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="stack gap-2" style={{ maxWidth: '58ch' }}>
      <h1 className="headline-large">{title}</h1>
      <p className="body-large text-muted">{subtitle}</p>
    </div>
  )
}

/**
 * Shown only for the two industries where a buyer is evaluating Wami against a real regulatory
 * obligation (HIPAA, SOX/FINRA) — the moment they need this fact is before they commit to the
 * trial, not after, at the pairing screen (`DisclosureNotice`) where it lives today. Reuses that
 * same canonical wording rather than writing new transport-risk copy that could drift from it.
 */
function TransportComplianceNotice() {
  return (
    <Banner tone="warn">
      <strong>{DISCLOSURE_TITLE}.</strong> {DISCLOSURE_SUMMARY} If you need to defend how patient
      or client communications reach WhatsApp to an auditor, read this before you continue —{' '}
      <Link to="/trust">the full disclosure is here</Link>.
    </Banner>
  )
}

function IndustryStep({
  selected,
  onSelect,
  onContinue,
}: {
  selected: IndustryId
  onSelect: (id: IndustryId) => void
  onContinue: () => void
}) {
  return (
    <div className="stack gap-6">
      <StepHeader
        title="What does your team do?"
        subtitle="This sets your compliance requirements and builds your workspace: the labels on your chats, the fields on a ticket, how fast each kind of message must be answered, and a set of canned replies."
      />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
        }}
      >
        {INDUSTRIES.map((industry) => (
          <button
            key={industry.id}
            type="button"
            className="selectable"
            aria-pressed={selected === industry.id}
            onClick={() => onSelect(industry.id)}
          >
            <div className="stack gap-2">
              <div className="row gap-2 wrap items-baseline">
                <span className="title-large">{industry.name}</span>
                {industry.compliance
                  .filter((c) => c !== 'none')
                  .map((c) => (
                    <Chip key={c} tone="warn">
                      {COMPLIANCE_LABELS[c]}
                    </Chip>
                  ))}
              </div>
              <p className="body-medium" style={{ opacity: 0.85 }}>
                {industry.description}
              </p>
              <span className="body-small" style={{ opacity: 0.7 }}>
                Starts on {getPlan(industry.presets.plan)?.name} · {industry.presets.seats} seats ·{' '}
                {industry.presets.numbers} numbers
              </span>
            </div>
          </button>
        ))}
      </div>

      {/* The chosen sector's setup is shown here rather than only at review, so the
          difference between two industries is visible while the choice is being made. */}
      <BlueprintPreview industryId={selected} />

      {(selected === 'healthcare' || selected === 'financial') && <TransportComplianceNotice />}

      <div className="row">
        <Button size="large" onClick={onContinue}>
          Continue with {getIndustry(selected)?.name}
        </Button>
      </div>
    </div>
  )
}

function ConfigureStep({
  config,
  onChange,
  onBack,
  onNext,
}: {
  config: QuoteInput
  onChange: (next: QuoteInput) => void
  onBack: () => void
  onNext: () => void
}) {
  const industry = getIndustry(config.industryId)!
  return (
    <div className="stack gap-6">
      <StepHeader
        title="Size it to your team"
        subtitle="These numbers are the defaults for your industry. Adjust anything, including your billing region — the price updates as you go."
      />
      <div
        className="pricing-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(300px, 1fr)',
          gap: '32px',
          alignItems: 'start',
        }}
      >
        <Card variant="outlined">
          <div className="stack gap-5">
            <Banner tone="info">{industry.presetRationale}</Banner>
            <PricingConfigurator
              input={config}
              onChange={onChange}
              showIndustry={false}
              showLocale
            />
          </div>
        </Card>

        <div className="stack gap-4" style={{ position: 'sticky', top: '88px' }}>
          <Card variant="filled">
            <QuoteSummary input={config} compact />
          </Card>
          <Button size="large" full onClick={onNext}>
            Review and confirm
          </Button>
          <Button variant="text" full onClick={onBack}>
            Back
          </Button>
        </div>
      </div>
    </div>
  )
}

function DatabaseStep({ onBack, onNext }: { onBack: () => void; onNext: () => void }) {
  const tenant = useQuery({ queryKey: ['tenant'], queryFn: api.tenant })
  const ready = tenant.data?.tenant?.status === 'ready'

  return (
    <div className="stack gap-6">
      <StepHeader
        title="Set up your database"
        subtitle="Every conversation, ticket and contact lives in its own private database — hosted by Wami by default, or in your own Supabase or Neon account if you'd rather. Set it up now, or skip ahead and finish this from any screen once you're in."
      />
      <TenantSetup />
      <div className="row gap-3">
        <Button variant="text" onClick={onBack}>
          Back
        </Button>
        <Button size="large" onClick={onNext}>
          {ready ? 'Continue' : 'Continue — set this up later'}
        </Button>
      </div>
    </div>
  )
}

function ReviewStep({
  config,
  industryId,
  localeId,
  onBack,
  onConfirm,
  pending,
  error,
}: {
  config: QuoteInput
  industryId: IndustryId
  localeId: LocaleId
  onBack: () => void
  onConfirm: () => void
  pending: boolean
  error?: string
}) {
  const industry = getIndustry(industryId)!
  const locale = getLocale(localeId)!
  const plan = getPlan(config.planId)!

  return (
    <div className="stack gap-6">
      <StepHeader
        title="Confirm your workspace"
        subtitle="Your trial runs for 14 days. Nothing is charged until it ends, and you can change any of this from billing at any time."
      />

      {/* Names the labels, fields and replies the industry choice creates, so the sector
          answer visibly does something rather than only moving the price. */}
      <BlueprintPreview industryId={industryId} />

      <div
        className="pricing-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(300px, 1fr)',
          gap: '32px',
          alignItems: 'start',
        }}
      >
        <Card variant="outlined">
          <div className="stack gap-5">
            <div className="stack gap-3">
              <h2 className="title-large">Configuration</h2>
              <dl className="stack gap-3" style={{ margin: 0 }}>
                <ReviewRow label="Industry" value={industry.name} />
                <ReviewRow label="Billing region" value={`${locale.name} (${locale.currency})`} />
                <ReviewRow label="Plan" value={plan.name} />
                <ReviewRow
                  label="Billing cycle"
                  value={config.billingCycle === 'annual' ? 'Annual' : 'Monthly'}
                />
                <ReviewRow label="Seats" value={config.seats.toLocaleString()} />
                <ReviewRow label="WhatsApp numbers" value={config.numbers.toLocaleString()} />
                <ReviewRow
                  label="Conversations"
                  value={`${config.conversations.toLocaleString()} per month`}
                />
              </dl>
            </div>

            <hr className="divider" />

            <div className="stack gap-3">
              <h2 className="title-large">Modules</h2>
              <div className="row gap-2 wrap">
                {config.addons.length === 0 ? (
                  <span className="body-medium text-muted">No additional modules selected.</span>
                ) : (
                  config.addons.map((a) => (
                    <Chip key={a} tone="assist">
                      {getAddon(a)?.name ?? a}
                    </Chip>
                  ))
                )}
              </div>
            </div>

            <hr className="divider" />

            <div className="stack gap-3">
              <h2 className="title-large">Templates we will install</h2>
              <div className="row gap-2 wrap">
                {industry.workflowTemplates.map((w) => (
                  <Chip key={w} tone="assist">
                    {w}
                  </Chip>
                ))}
              </div>
              <p className="body-small text-muted">
                Pre-built automations for {industry.name.toLowerCase()}. Edit or remove them once your
                workspace is live.
              </p>
            </div>
          </div>
        </Card>

        <div className="stack gap-4" style={{ position: 'sticky', top: '88px' }}>
          <Card variant="filled">
            <QuoteSummary input={config} />
          </Card>
          {(industryId === 'healthcare' || industryId === 'financial') && <TransportComplianceNotice />}
          {error && <Banner tone="error">{error}</Banner>}
          <Button size="large" full onClick={onConfirm} disabled={pending}>
            {pending ? 'Setting up your workspace…' : 'Start 14-day trial'}
          </Button>
          <Button variant="text" full onClick={onBack} disabled={pending}>
            Back
          </Button>
          <p className="body-small text-muted" style={{ textAlign: 'center' }}>
            No card required. Cancel any time during the trial.
          </p>
        </div>
      </div>
    </div>
  )
}

function ReviewRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="row between gap-4">
      <dt className="body-medium text-muted">{label}</dt>
      <dd className="body-medium tabular" style={{ margin: 0, fontWeight: 600 }}>
        {value}
      </dd>
    </div>
  )
}
